/**
 * Contratos de entrada (Zod) de la originación: solicitud, simulación,
 * transiciones, comité, desembolso, garantías, fiadores y análisis.
 */
import { z } from 'zod';
import { PAYMENT_FREQUENCIES } from '../credit/frequency.js';
import { keyedEnum } from '../catalogs/enum.js';
import { LOAN_STATUSES } from '../credit/lifecycle.js';
import { GUARANTEE_TYPES, LOAN_PURPOSES, MAX_PRINCIPAL_GRACE_PERIODS, MAX_TERM_PERIODS } from '../credit/product.js';
import { DEFAULT_REPAYMENT_METHOD, REPAYMENT_METHODS, repaymentTermsViolations } from '../credit/repayment.js';
import { INTEREST_METHOD, INTEREST_METHODS } from '../credit/schedule.js';
import { addressSchema } from './client.js';
import { basisPointsSchema, coordinatesSchema, cuiSchema, idSchema, isoDateSchema, moneySchema, optionalText, REASON_MIN_LENGTH, TEXT_MAX, phoneSchema, positiveMoneySchema, clientRequestIdSchema, capturedAtSchema, } from './common.js';
/** Plan de capital personalizado: un porcentaje (puntos básicos) por cuota. */
export const principalPlanSchema = z.array(basisPointsSchema).min(1).max(MAX_TERM_PERIODS);
/** Condiciones financieras comunes a la solicitud y la simulación. */
const termsFields = {
    principal: positiveMoneySchema,
    annualInterestRate: basisPointsSchema,
    frequency: z.enum(PAYMENT_FREQUENCIES),
    termPeriods: z.number().int().min(1).max(MAX_TERM_PERIODS),
    principalGracePeriods: z.number().int().min(0).max(MAX_PRINCIPAL_GRACE_PERIODS).default(0),
    repaymentMethod: z.enum(REPAYMENT_METHODS).default(DEFAULT_REPAYMENT_METHOD),
    /** Porcentaje del capital por cuota (puntos básicos); solo en plan personalizado. */
    principalPlan: principalPlanSchema.nullable().default(null),
    firstPaymentDate: isoDateSchema,
};
/** Nueva solicitud de crédito. */
export const CreateLoanApplicationSchema = z.object({
    /** Idempotencia de la solicitud capturada sin conexión. */
    clientRequestId: clientRequestIdSchema,
    /** Momento de la solicitud en el teléfono: fecha de la solicitud. */
    capturedAt: capturedAtSchema,
    /**
     * Última modificación del producto que conocía el teléfono al simular con
     * el cliente. Si el producto cambió después, la solicitud se rechaza para
     * volver a simular: el cliente debe conocer las condiciones reales.
     */
    quotedProductUpdatedAt: z.string().datetime({ offset: true }).optional(),
    clientId: idSchema,
    businessId: idSchema.optional(),
    productId: idSchema,
    purpose: z.enum(LOAN_PURPOSES),
    purposeDescription: optionalText(TEXT_MAX.shortText),
    guaranteeType: z.enum(GUARANTEE_TYPES),
    advisorId: idSchema.optional(),
    notes: optionalText(TEXT_MAX.longText),
    ...termsFields,
});
/** Edición de una solicitud en originación (el cliente no cambia). */
export const UpdateLoanApplicationSchema = CreateLoanApplicationSchema.omit({ clientId: true, clientRequestId: true, capturedAt: true, quotedProductUpdatedAt: true }).partial();
/**
 * Simulación libre (sin producto obligatorio): lo que el asesor muestra al
 * cliente en campo. Valida la coherencia de la forma de pago porque, sin
 * producto, no hay otra regla que la revise.
 */
export const SimulateLoanSchema = z
    .object({
    productId: idSchema.optional(),
    interestMethod: z.enum(INTEREST_METHODS).default(INTEREST_METHOD.declining_balance),
    taxOnInterest: basisPointsSchema.optional(),
    originationFeeRate: basisPointsSchema.default(0),
    fixedCharges: moneySchema.default(0),
    shiftToBusinessDay: z.boolean().default(true),
    ...termsFields,
})
    .superRefine((terms, ctx) => {
    for (const message of repaymentTermsViolations(terms)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['principalPlan'] });
    }
});
/** Cambio manual de etapa del préstamo. */
export const TransitionLoanSchema = z.object({
    toStatus: z.enum(LOAN_STATUSES),
    notes: optionalText(TEXT_MAX.notes),
});
/** Decisiones posibles del comité de crédito. */
export const COMMITTEE_DECISIONS = ['approved', 'rejected'];
/** Catálogo con clave de decisiones de comité: `COMMITTEE_DECISION.approved`. */
export const COMMITTEE_DECISION = keyedEnum(COMMITTEE_DECISIONS);
/** Resolución del comité; el rechazo debe explicarse al cliente. */
export const CommitteeDecisionSchema = z
    .object({
    decision: z.enum(COMMITTEE_DECISIONS),
    /** El comité puede aprobar condiciones distintas a las solicitadas. */
    approvedPrincipal: positiveMoneySchema.optional(),
    approvedTermPeriods: z.number().int().min(1).max(MAX_TERM_PERIODS).optional(),
    approvedAnnualRate: basisPointsSchema.optional(),
    /** Nuevo plan personalizado cuando el comité cambia el plazo de un préstamo estacional. */
    approvedPrincipalPlan: principalPlanSchema.optional(),
    conditions: optionalText(TEXT_MAX.longText),
    reason: optionalText(TEXT_MAX.longText),
    minutesReference: optionalText(TEXT_MAX.reference),
})
    .refine((d) => d.decision === COMMITTEE_DECISION.approved || (d.reason?.length ?? 0) >= REASON_MIN_LENGTH, {
    message: `Explica el motivo del rechazo (mínimo ${String(REASON_MIN_LENGTH)} caracteres); el cliente tiene derecho a conocerlo`,
    path: ['reason'],
});
/** Medios de desembolso. */
export const DISBURSEMENT_METHODS = ['cash', 'bank_transfer', 'check', 'bank_deposit'];
/** Catálogo con clave de medios de desembolso: `DISBURSEMENT_METHOD.cash`. */
export const DISBURSEMENT_METHOD = keyedEnum(DISBURSEMENT_METHODS);
/** Etiquetas en español de cada medio de desembolso. */
export const DISBURSEMENT_METHOD_LABELS = {
    cash: 'Efectivo en caja',
    bank_transfer: 'Transferencia bancaria',
    check: 'Cheque',
    bank_deposit: 'Depósito a cuenta',
};
/** Longitud máxima del número de cuenta bancaria del cliente. */
const BANK_ACCOUNT_MAX_LENGTH = 60;
/** Desembolso de un préstamo aprobado. */
export const DisburseLoanSchema = z.object({
    disbursementDate: isoDateSchema.optional(),
    firstPaymentDate: isoDateSchema,
    method: z.enum(DISBURSEMENT_METHODS),
    reference: optionalText(TEXT_MAX.reference),
    bankAccount: optionalText(BANK_ACCOUNT_MAX_LENGTH),
    notes: optionalText(TEXT_MAX.notes),
});
/** Castigo de un préstamo (requiere motivo). */
export const WriteOffLoanSchema = z.object({
    reason: z.string().trim().min(REASON_MIN_LENGTH).max(TEXT_MAX.longText),
});
/** Reasignación de asesor o gestor de cobro (`null` quita el gestor). */
export const ReassignLoanSchema = z.object({
    advisorId: idSchema.optional(),
    collectorId: idSchema.nullable().optional(),
});
/** Tipos de bien dado en garantía. */
export const COLLATERAL_TYPES = [
    'real_estate',
    'vehicle',
    'motorcycle',
    'machinery',
    'equipment',
    'livestock',
    'inventory',
    'jewelry',
    'other',
];
/** Etiquetas en español de cada tipo de garantía real. */
export const COLLATERAL_TYPE_LABELS = {
    real_estate: 'Inmueble',
    vehicle: 'Vehículo',
    motorcycle: 'Motocicleta',
    machinery: 'Maquinaria',
    equipment: 'Equipo',
    livestock: 'Ganado',
    inventory: 'Inventario / mercadería',
    jewelry: 'Joyas',
    other: 'Otro',
};
/** Longitud máxima de los datos registrales (finca/folio/libro, placa o VIN). */
const REGISTRY_DATA_MAX_LENGTH = 300;
/** Registro y avalúo de una garantía real. */
export const CollateralSchema = z.object({
    type: z.enum(COLLATERAL_TYPES),
    description: z.string().trim().min(REASON_MIN_LENGTH).max(TEXT_MAX.notes),
    ownerName: z.string().trim().min(3).max(TEXT_MAX.name),
    ownerCui: cuiSchema.optional(),
    location: z.string().trim().min(3).max(TEXT_MAX.shortText),
    /** Registro de la Propiedad (finca/folio/libro) o placa / VIN del vehículo. */
    registryData: optionalText(REGISTRY_DATA_MAX_LENGTH),
    marketValue: positiveMoneySchema,
    forcedSaleValue: positiveMoneySchema.optional(),
    appraisalDate: isoDateSchema,
    appraisalNotes: optionalText(TEXT_MAX.longText),
    coordinates: coordinatesSchema.optional(),
});
/** Registro de un fiador. */
export const GuarantorSchema = z
    .object({
    firstNames: z.string().trim().min(2).max(TEXT_MAX.personName),
    lastNames: z.string().trim().min(2).max(TEXT_MAX.personName),
    cui: cuiSchema,
    phone: phoneSchema,
    relationship: z.string().trim().min(2).max(TEXT_MAX.label),
    occupation: z.string().trim().min(2).max(TEXT_MAX.name),
    monthlyIncome: moneySchema,
    notes: optionalText(TEXT_MAX.notes),
})
    .merge(addressSchema);
/** Recomendaciones posibles del estudio financiero. */
export const ASSESSMENT_RECOMMENDATIONS = ['approve', 'approve_with_changes', 'decline'];
/** Etiquetas en español de cada recomendación. */
export const ASSESSMENT_RECOMMENDATION_LABELS = {
    approve: 'Aprobar',
    approve_with_changes: 'Aprobar con modificaciones',
    decline: 'Declinar',
};
/** Estudio financiero (flujo de caja y balance) del solicitante. */
export const FinancialAssessmentSchema = z.object({
    businessIncome: moneySchema,
    otherIncome: moneySchema.default(0),
    businessExpenses: moneySchema,
    householdExpenses: moneySchema,
    existingDebtPayments: moneySchema.default(0),
    totalAssets: moneySchema.optional(),
    totalLiabilities: moneySchema.optional(),
    inventoryValue: moneySchema.optional(),
    /**
     * Ingreso neto esperado en el período de la cuota más alta (cosecha,
     * temporada). Obligatorio en la práctica para créditos con cuota pico.
     */
    peakPeriodIncome: moneySchema.optional(),
    recommendation: z.enum(ASSESSMENT_RECOMMENDATIONS),
    recommendedPrincipal: positiveMoneySchema.optional(),
    justification: z.string().trim().min(REASON_MIN_LENGTH).max(TEXT_MAX.narrative),
});
/** Resultado de la verificación de campo. */
export const FieldVerificationSchema = z.object({
    visitDate: isoDateSchema,
    businessVerified: z.boolean(),
    homeVerified: z.boolean(),
    referencesVerified: z.boolean(),
    coordinates: coordinatesSchema.optional(),
    observations: z.string().trim().min(REASON_MIN_LENGTH).max(TEXT_MAX.narrative),
    characterAssessment: optionalText(TEXT_MAX.longText),
});
/** Agrupaciones de estados que usa el filtro `stage` del listado de préstamos. */
export const LOAN_STAGES = ['origination', 'portfolio', 'closed'];
/** Catálogo con clave de agrupaciones: `LOAN_STAGE.portfolio`. */
export const LOAN_STAGE = keyedEnum(LOAN_STAGES);
/** Valores booleanos tal como llegan en la query string. */
export const BOOLEAN_QUERY_VALUES = ['true', 'false'];
/** Filtros del listado de préstamos. */
export const loanListQuerySchema = z.object({
    status: z.enum(LOAN_STATUSES).optional(),
    stage: z.enum(LOAN_STAGES).optional(),
    clientId: idSchema.optional(),
    productId: idSchema.optional(),
    branchId: idSchema.optional(),
    advisorId: idSchema.optional(),
    delinquent: z.enum(BOOLEAN_QUERY_VALUES).optional(),
});
//# sourceMappingURL=loan.js.map